import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import getStateManager from '../StateManager';
import { useTabSettings, writeTempSettings } from '../Tabs/settingFunctions';
import { controllerClockDraftFields, migrateControllerClockDrafts,
  supportsSystemClockRelocation, withoutControllerClock } from '../Utils/system_settings_utils';
import { createNestedSettingsDraft, createSettingsDraftStore } from '../Utils/settings_draft';

export function useSystemClockDraftMigration(controller, selectedParent, enabled = true) {
  const relocated = supportsSystemClockRelocation(controller);
  // As with CO2, this effect must precede the page's draft-restoration effect.
  useEffect(() => {
    if (!enabled || !relocated) return;
    const migrated = migrateControllerClockDrafts(controller, selectedParent);
    if (!migrated.length) return;
    // Persist the destination first, then the cleared sources. Legacy zone keys
    // lack controller identity and must remain untouched.
    writeTempSettings(controller);
    migrated.filter((parent) => parent !== controller && parent.controller_id !== undefined)
      .forEach(writeTempSettings);
  }, [controller, selectedParent, enabled, relocated]);
}

export function createTimingSettingsDraft(parent) {
  const nested = createNestedSettingsDraft(createSettingsDraftStore(parent, 'timing',
    () => writeTempSettings(parent)));
  return {
    ...nested,
    restore: (baseline) => {
      const saved = nested.read();
      const restored = nested.restore(baseline);
      ['event_timer', 'interval_timer'].forEach((path) => {
        const timers = saved[path];
        // Older field-only drafts remain sparse patches. Complete retained lists
        // replace readback, including an empty list or a shorter local collection.
        if (Array.isArray(timers) && Array.from(timers).every((timer) => timer?.id !== undefined))
          restored[path] = _.cloneDeep(timers);
      });
      for (const [key, value] of Object.entries(restored)) {
        if (value === '_deleted_') delete restored[key];
      }
      return restored;
    }
  };
}

export function useTimingSettings() {
  const params = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  useSystemClockDraftMigration(controller, parent);
  const draft = useMemo(() => createTimingSettingsDraft(parent), [parent]);
  return useTabSettings(null, controllerClockDraftFields,
    supportsSystemClockRelocation(controller) ? withoutControllerClock : undefined, null, draft);
}
